import { Button, Modal, notification, Progress } from 'antd'
import styles from './UploadCsvModal.module.css'
import { DownloadOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { FileImporter, defineImportConfig, rowsToCsvFile } from '@nuport/file-import'
import { Tasks } from '../../services/api/firebase'
import { useSelector } from 'react-redux'
import { convertEnumToString } from '../../utils'
import { beginOperation, endOperation } from '../../utils/idle-session'

// The backend parses CSV, whatever the user uploaded (CSV, XLSX or XLS), so the file it receives is
// always named as one.
const toCsvFileName = (fileName, fallback) => {
	const base = fileName ? fileName.replace(/\.[^.]+$/, '') : fallback
	return `${base}.csv`
}

const UploadCsvModal = ({
	title,
	type,
	fields = [],
	loading = false,
	firstStepPrompt,
	secondStepPrompt,
	visible,
	onCancel,
	downloadTask,
	uploadTask,
	onComplete,
	importOnly = false, // Flag indicates we do not proceed with uploading the data.
	disableSteps = false,
	allowCustom = false // Keep columns that match no field, under their own header.
}) => {
	const { userProfile } = useSelector(state => state.authReducer)
	const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
	const [file, setFile] = useState()
	const [importedValues, setImportedValues] = useState([])
	const [isUploadingCsv, setIsUploadingCsv] = useState(false)
	const [logs, setLogs] = useState([])
	const [taskId, setTaskId] = useState()
	const [progress, setProgress] = useState(0)
	const [status, setStatus] = useState()

	const importConfig = {
		name: convertEnumToString(type),
		slug: type,
		fields,
		allowUnmappedColumns: allowCustom
	}

	useEffect(() => {
		if (taskId) {
			const unsubscribeTask = Tasks.listenToTask(
				userProfile.companyId,
				taskId,
				task => {
					setProgress(task.progress)
					switch (task.loadState) {
						case 'SUCCEEDED':
							setStatus('completed')
							unsubscribeTask()
							if (task.logs) {
								setLogs(logs => [...logs, ...task.logs])
							}
							break
						case 'FAILED':
							setStatus('exception')
							unsubscribeTask()
							break
						default:
							setStatus('active')
							if (task.logs) {
								setLogs(logs => [...logs, ...task.logs])
							}
							break
					}
				}
			)
			return () => {
				unsubscribeTask()
			}
		}
	}, [taskId])

	// A bulk upload goes on processing after the file itself has been sent. While it runs the user
	// is waiting on it, and the session must not time out underneath them.
	useEffect(() => {
		if (status !== 'active') return
		const operationId = beginOperation()
		return () => endOperation(operationId)
	}, [status])

	const downloadCsv = async () => {
		setIsDownloadingCsv(true)
		if (downloadTask) {
			await downloadTask()
		}
		setIsDownloadingCsv(false)
	}

	const onUploadFile = async () => {
		try {
			if (importOnly) {
				if (onComplete) {
					onComplete(importedValues)
				}
			} else {
				setIsUploadingCsv(true)
				setLogs([])
				if (uploadTask) {
					const response = await uploadTask(file)
					const { taskId, errorMessages } = response.data
					setLogs(errorMessages)
					if (errorMessages.length === 0) {
						setTaskId(taskId)
						setStatus('active')
					}
				}
			}
		} catch (e) {
			setLogs([])
			notification.error({
				message: 'File Upload Failed',
				description: e.message,
				placement: 'bottomLeft'
			})
		} finally {
			setIsUploadingCsv(false)
		}
	}

	const onOk = () => {
		if (onComplete) {
			onComplete()
		}
		onCancel()
	}

	// Only the rows that passed validation leave the importer. Each one is keyed by field key and
	// already cast to the field type; empty cells are undefined.
	const onImport = ({ validRows, fileName }) => {
		const records = validRows.map(row => row.values)
		const csvFileName = toCsvFileName(fileName, type)
		if (importOnly) {
			setImportedValues(records)
			setFile({ name: csvFileName })
		} else {
			// Same bytes the backend already receives: one column per field, headed by the field key.
			setFile(rowsToCsvFile(records, {
				headers: 'key',
				fields: defineImportConfig(importConfig).fields,
				includeExtraKeys: allowCustom,
				fileName: csvFileName
			}))
		}
	}

	const onImportError = (error) => {
		console.error(error)
		notification.error({
			message: 'Error',
			description: `${error.message}`,
			placement: 'bottomRight'
		})
	}

	const renderTrigger = (open) => {
		return (
			<Button
				size='large'
				type='primary'
				shape='round'
				onClick={open}
			>
				Get Started
			</Button>
		)
	}

	const isLoading = isUploadingCsv || (taskId !== undefined && status === 'active')
	const isFinished = taskId !== undefined && status !== 'active'

	return (
		<Modal
			title={title}
			visible={visible}
			onCancel={onCancel}
			maskClosable={false}
			okText={isUploadingCsv ? 'Validating' : isLoading ? 'Uploading...' : isFinished ? 'OK' : importOnly ? 'Import' : 'Upload'}
			okButtonProps={{
				loading: isLoading,
				disabled: !file
			}}
			closable={!isLoading}
			onOk={isFinished ? onOk : onUploadFile}
			cancelButtonProps={{
				disabled: isLoading
			}}
			width={720}
		>
			<div>
				<div className={styles.uploadContainer}>
					<div style={{ display: 'flex' }}>
						<div className={styles.item}>
							<div>
								{
									!disableSteps &&
									<span style={{ marginRight: 12 }}><b>1.</b></span>
								}
								<span>{firstStepPrompt}</span>
							</div>
							{
								!disableSteps &&
								<Button
									onClick={downloadCsv}
									loading={isDownloadingCsv}
									style={{ marginTop: 24 }}
									icon={<DownloadOutlined />}
								>
									Download CSV
								</Button>
							}
						</div>
						{
							!disableSteps &&
							<div className={styles.item}>
								<div>
									<span style={{ marginRight: 12 }}><b>2.</b></span>
									<span>{secondStepPrompt}</span>
								</div>
							</div>
						}
					</div>
					<div className={styles.uploadItem}>
						<div>
							<div>
								{
									!disableSteps &&
									<span style={{ marginRight: 12 }}><b>3.</b></span>
								}
								<span>Start importing your data.</span>
							</div>
							{
								loading ? <div className={styles.import}>Initializing...</div> :
									<div className={styles.import}>
										<FileImporter
											mode='modal'
											config={importConfig}
											trigger={renderTrigger}
											onSubmit={onImport}
											onError={onImportError}
										/>
										{
											file &&
											<div className={styles.file}>
												{file.name}
											</div>
										}
									</div>
							}
						</div>
					</div>
				</div>
				{
					taskId &&
					<Progress percent={progress} format={percent => `${Math.round(percent)} %`} size='small' status={status} style={{ marginTop: 24 }} />
				}
				{
					logs && logs.length > 0 ?
						<div className={styles.output}>
							{
								logs.map((data, index) => {
									return (
										<div key={index} className={styles.outputRow}>
											{data}
										</div>
									)
								})
							}
						</div> : null
				}
			</div>
		</Modal>
	)
}

export default UploadCsvModal
